import type sv from '../sv/editor'

const editor: typeof sv = {
  placeholder: {
    document: 'Start writing here...',
    title: 'Document title...',
    mini: 'Write here...',
  },
  loadingDocument: 'Loading document...',
  save: 'Save',
  saving: 'Saving...',
  toolbar: {
    undo: 'Undo',
    redo: 'Redo',
    defaultFont: 'Default',
    fontSize: 'Font size',
    decreaseFontSize: 'Decrease font size',
    increaseFontSize: 'Increase font size',
    heading1: 'Heading 1',
    heading2: 'Heading 2',
    heading3: 'Heading 3',
    bold: 'Bold',
    italic: 'Italic',
    underline: 'Underline',
    strikethrough: 'Strikethrough',
    code: 'Code',
    link: 'Link',
    highlight: 'Highlight',
    chooseHighlightColor: 'Choose highlight colour',
    highlightColor: {
      yellow: 'Yellow',
      green: 'Green',
      blue: 'Blue',
      red: 'Red',
    },
    removeHighlight: 'Remove highlight',
    textColor: 'Text colour',
    alignLeft: 'Align left',
    alignCenter: 'Align centre',
    alignRight: 'Align right',
    outdent: 'Decrease indent',
    indent: 'Increase indent',
    list: 'List',
    chooseListType: 'Choose list type',
    bulletList: 'Bullet list',
    numberedList: 'Numbered list',
    checkList: 'Checklist',
    insertImage: 'Insert image',
    table: 'Table',
    tableAndLayout: 'Table & layout',
    insertTable: 'Insert table',
    columnLayout: 'Column layout',
    insertBlock: 'Insert block',
    quote: 'Quote',
    codeBlock: 'Code block',
    horizontalRule: 'Horizontal rule',
    pageBreak: 'Page break',
    print: 'Print',
    download: 'Download',
    downloadPdf: 'Download PDF',
    downloadWord: 'Download Word',
    comments: 'Comments',
  },
  linkDialog: {
    title: 'Insert link',
    description: 'Enter the URL for the link.',
    urlLabel: 'URL',
    urlPlaceholder: 'https://example.com',
    cancel: 'Cancel',
    insert: 'Insert',
  },
  tableDialog: {
    title: 'Insert table',
    description: 'Choose the number of rows and columns.',
    rows: 'Rows',
    columns: 'Columns',
    cancel: 'Cancel',
    insert: 'Insert',
  },
  layoutDialog: {
    title: 'Insert column layout',
    description: 'Choose a layout template.',
    twoEqual: '2 columns (equal)',
    twoSplit: '2 columns (25/75)',
    threeEqual: '3 columns (equal)',
    threeSplit: '3 columns (25/50/25)',
    fourEqual: '4 columns (equal)',
  },
  colourPicker: {
    reset: 'Reset to default colour',
  },
  table: {
    actions: 'Table actions',
    addColumn: 'Add column',
    addRow: 'Add row',
    mergeCells: 'Merge cells',
    unmergeCell: 'Split cell',
    insertRowAbove_one: 'Insert row above',
    insertRowAbove_other: 'Insert {{count}} rows above',
    insertRowBelow_one: 'Insert row below',
    insertRowBelow_other: 'Insert {{count}} rows below',
    insertColumnLeft_one: 'Insert column to the left',
    insertColumnLeft_other: 'Insert {{count}} columns to the left',
    insertColumnRight_one: 'Insert column to the right',
    insertColumnRight_other: 'Insert {{count}} columns to the right',
    deleteColumn: 'Delete column',
    deleteRow: 'Delete row',
    deleteTable: 'Delete table',
    addRowHeader: 'Add row header',
    removeRowHeader: 'Remove row header',
    addColumnHeader: 'Add column header',
    removeColumnHeader: 'Remove column header',
  },
  code: {
    plainText: 'Plain text',
    copy: 'Copy code',
  },
  comments: {
    title: 'Comments',
    threadCount_one: '{{count}} thread',
    threadCount_other: '{{count}} threads',
    addComment: 'Add comment',
    placeholder: 'Write a comment...',
    replyPlaceholder: 'Write a reply...',
    cancel: 'Cancel',
    submit: 'Comment',
    empty: 'No comments yet',
    resolvedHeading: 'Resolved ({{count}})',
    reply: 'Reply',
    resolve: 'Resolve',
    reopen: 'Reopen',
    delete: 'Delete',
    hiddenReplies_one: '{{count}} reply hidden',
    hiddenReplies_other: '{{count}} replies hidden',
    collapseReplies: 'Hide replies',
    replyCount_one: '{{count}} reply',
    replyCount_other: '{{count}} replies',
    showResolved: 'Show resolved ({{count}})',
    hideResolved: 'Hide resolved',
    hideAll: 'Hide all comments',
    hiddenBadge: 'Comments hidden',
    menuTitle: 'Comments',
    resolved: 'Resolved',
    textChanged: 'The text has changed',
    wholeBlock: 'Whole block',
    removedBlock: 'The block was removed',
    newComment: 'New comment',
    allComments: 'All comments',
    openCount_one: '{{count}} open',
    openCount_other: '{{count}} open',
    submitHint: 'Ctrl+Enter sends',
    bold: 'Bold',
    italic: 'Italic',
    underline: 'Underline',
    link: 'Link',
    linkUrl: 'Link address',
    linkApply: 'Add',
    linkRemove: 'Remove link',
    discardTitle: 'Discard the comment?',
    discardBody: 'You have an unsaved comment.',
    discardConfirm: 'Discard',
    keepEditing: 'Keep writing',
    errorHasReplies: 'Delete the replies before deleting the comment',
    errorMissing: 'The comment no longer exists',
    errorGeneric: 'The comment could not be saved',
    deleteTitle: 'Delete the comment?',
    deleteConfirm: 'Delete',
    loadingComposer: 'Loading…',
  },
  mentions: {
    noMatch: 'No member matches',
    former: 'No longer a project member',
  },
  blocks: {
    connecting: 'Connecting…',
    offline: 'Disconnected – reconnecting…',
    exportPdf: 'Download as PDF',
    uploadFile: 'Upload file',
    fileUrlPlaceholder: 'Paste a link…',
    embed: 'Embed',
    uploadFailed: 'Upload failed',
    print: 'Print',
    exportDocx: 'Download as Word',
    download: 'Download',
    exportFailed: 'Export failed',
    loading: 'Loading document…',
    agentWriting: 'HUB is writing…',
    pageBreak: 'Page break',
    askHub: 'Ask HUB about this block',
    comment: 'Comment',
    blocksSelected_one: '{{count}} block selected',
    blocksSelected_other: '{{count}} blocks selected',
    clearSelection: 'Clear selection',
    changedBlocks: 'Changed blocks',
    undoTurn: 'Undo',
    undone: 'Changes undone',
    undoFailed: 'Could not undo',
    jumpTo: 'Go to block',
    bubbleTitle: 'HUB',
    bubbleHint: 'Ask HUB about the document',
    bubblePlaceholder: 'Ask HUB or request changes…',
    bubbleWithSelection: 'What would you like to do with the selection?',
    quickImprove: 'Improve',
    quickSummarize: 'Summarize',
    quickContinue: 'Continue writing',
    quickImprovePrompt:
      'Improve the wording of the selected blocks without changing their meaning.',
    quickSummarizePrompt:
      'Summarize the selected blocks into a few sentences and replace them with the summary.',
    quickContinuePrompt:
      'Continue writing after the selected blocks in the same style, a couple of paragraphs.',
    collapse: 'Minimize',
    open: 'Open HUB',
    commentsForBlock: 'Comments on this block',
    removedBlocks: 'Removed blocks',
    sendToHub: 'Send to HUB',
    noOpenComments: 'No open comments',
    resolved: 'Resolved',
  },
}

export default editor
